/**
 * @fileoverview Componente responsável por validar o agente de IA em um chat de teste.
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Send,
  Trash2,
  Image,
  Mic,
  FileText,
  Bot,
  User,
} from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const accent = "#5B7CFF";
  const purple = "#9D7CFF";
  const border = isDark ? "rgba(91, 124, 255, 0.15)" : "rgba(0, 0, 0, 0.08)";
  const surfaceLow = isDark ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.01)";
  const surfaceMid = isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.04)";
  const textPrimary = isDark ? "#e2e8f0" : "#1e293b";
  const textMuted = isDark ? "#94a3b8" : "#64748b";
  const textFaint = isDark ? "#64748b" : "#94a3b8";
  const borderSoft = isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)";

  return {
    container: {
      display: "flex",
      flexDirection: "column",
      height: "60vh",
      maxHeight: 600,
      minHeight: 400,
    },
    chatHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 16px",
      borderBottom: `1px solid ${border}`,
      background: surfaceLow,
      borderRadius: "14px 14px 0 0",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: "0.85rem",
      fontWeight: 600,
      color: textMuted,
    },
    messagesArea: {
      flex: 1,
      overflowY: "auto",
      padding: 16,
      display: "flex",
      flexDirection: "column",
      gap: 12,
      background: isDark ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.02)",
      "&::-webkit-scrollbar": { width: 6 },
      "&::-webkit-scrollbar-thumb": { background: "rgba(91, 124, 255, 0.3)", borderRadius: 3 },
    },
    emptyState: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      gap: 12,
      color: textFaint,
      "& svg": { opacity: 0.4 },
    },
    emptyText: { fontSize: "0.9rem", textAlign: "center", lineHeight: 1.6 },
    messageRow: { display: "flex", gap: 8, maxWidth: "85%" },
    messageRowUser: { alignSelf: "flex-end", flexDirection: "row-reverse" },
    messageRowBot: { alignSelf: "flex-start" },
    avatar: {
      width: 32, height: 32, borderRadius: "50%",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, marginTop: 2,
    },
    avatarUser: {
      background: isDark ? "rgba(91, 124, 255, 0.2)" : "rgba(91, 124, 255, 0.1)",
      color: accent,
    },
    avatarBot: {
      background: isDark ? "rgba(157, 124, 255, 0.2)" : "rgba(157, 124, 255, 0.1)",
      color: purple,
    },
    bubble: {
      padding: "10px 14px", borderRadius: 16, fontSize: "0.875rem",
      lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word",
    },
    bubbleUser: {
      background: isDark ? "rgba(91, 124, 255, 0.15)" : "rgba(91, 124, 255, 0.08)",
      color: textPrimary, borderBottomRightRadius: 4,
      border: isDark ? "1px solid rgba(91, 124, 255, 0.2)" : "1px solid rgba(91, 124, 255, 0.12)",
    },
    bubbleBot: {
      background: surfaceMid, color: textPrimary, borderBottomLeftRadius: 4,
      border: `1px solid ${borderSoft}`,
    },
    mediaPreview: {
      maxWidth: 200, maxHeight: 200, borderRadius: 12,
      marginBottom: 6, objectFit: "cover", cursor: "pointer",
    },
    fileAttachment: {
      display: "flex", alignItems: "center", gap: 8,
      padding: "8px 12px", borderRadius: 10, marginBottom: 6,
      fontSize: "0.8rem", background: surfaceMid,
      border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
      color: textMuted,
    },
    typingIndicator: {
      display: "flex", alignItems: "center", gap: 8,
      padding: "8px 14px", alignSelf: "flex-start",
      color: textMuted, fontSize: "0.8rem",
    },
    typingDots: {
      display: "flex", gap: 3,
      "& span": {
        width: 6, height: 6, borderRadius: "50%",
        background: purple, animation: "$typing 1.4s infinite ease-in-out",
        "&:nth-child(2)": { animationDelay: "0.2s" },
        "&:nth-child(3)": { animationDelay: "0.4s" },
      },
    },
    "@keyframes typing": {
      "0%, 80%, 100%": { opacity: 0.3, transform: "scale(0.8)" },
      "40%": { opacity: 1, transform: "scale(1)" },
    },
    inputArea: {
      display: "flex", flexDirection: "column", gap: 0,
      borderTop: `1px solid ${border}`, background: surfaceLow,
      borderRadius: "0 0 14px 14px",
    },
    attachmentName: { flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    inputRow: { display: "flex", alignItems: "flex-end", gap: 8, padding: "10px 16px" },
    textField: {
      flex: 1,
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
        background: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
        border: `1px solid ${border}`,
        fontSize: "0.875rem",
        "& fieldset": { border: "none" },
      },
      // O multiline do MUI v4 aplica padding 18.5px no ROOT (deixava o campo alto);
      // zera no root e usa um padding compacto no textarea.
      "& .MuiOutlinedInput-multiline": { padding: 0 },
      "& .MuiOutlinedInput-inputMultiline": { padding: "8px 14px", color: isDark ? "#f1f5f9" : "#1a1a2e" },
    },
    sendBtn: {
      padding: 8, borderRadius: 12,
      background: isDark ? "#374151" : "#4b5563",
      color: "#fff", marginBottom: 2, transition: "all 0.2s",
      "&:hover": { background: isDark ? "#4b5563" : "#374151", transform: "scale(1.05)" },
      "&:disabled": { opacity: 0.5, transform: "none" },
    },
  };
});

const MEDIA_TYPE_ICONS = {
  image: Image,
  audio: Mic,
  document: FileText,
  video: FileText,
};

const TestChatTab = ({ agentId, agentName }) => {
  const classes = useStyles();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, loading, scrollToBottom]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = {
      id: Date.now(), role: "user",
      content: text,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    const payload = { message: text, history };

    try {
      const { data } = await api.post(`/ai-agents/${agentId}/test-chat`, payload, { timeout: 180000 });
      for (let i = 0; i < data.messages.length; i++) {
        setMessages((prev) => [...prev, {
          id: Date.now() + i + 1, role: "assistant",
          content: data.messages[i],
          confidence: data.confidence,
          wouldNeedApproval: data.wouldNeedApproval,
          files: i === 0 ? data.files : undefined,
          debug: i === 0 ? data.debug : undefined,
          timestamp: Date.now(),
        }]);
      }
    } catch (err) {
      toastError(err);
      setMessages((prev) => [...prev, {
        id: Date.now() + 999, role: "assistant",
        content: "Erro ao processar mensagem. Verifique se o agente tem um prompt configurado.",
        isError: true, timestamp: Date.now(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleClear = () => { setMessages([]); setInput(""); };

  const renderMessage = (msg) => {
    const isUser = msg.role === "user";
    const Icon = isUser ? User : Bot;
    return (
      <div key={msg.id} className={`${classes.messageRow} ${isUser ? classes.messageRowUser : classes.messageRowBot}`}>
        <div className={`${classes.avatar} ${isUser ? classes.avatarUser : classes.avatarBot}`}>
          <Icon size={16} />
        </div>
        <div>
          {msg.mediaPreview && msg.mediaType === "image" && (
            <img src={msg.mediaPreview} alt="preview" className={classes.mediaPreview}
              onClick={() => window.open(msg.mediaPreview, "_blank")} />
          )}
          {msg.originalName && msg.mediaType !== "image" && (
            <div className={classes.fileAttachment}>
              {React.createElement(MEDIA_TYPE_ICONS[msg.mediaType] || FileText, { size: 14 })}
              <span className={classes.attachmentName}>{msg.originalName}</span>
            </div>
          )}
          <div
            className={`${classes.bubble} ${isUser ? classes.bubbleUser : classes.bubbleBot}`}
            style={msg.isError ? { borderColor: "rgba(239, 68, 68, 0.3)", background: "rgba(239, 68, 68, 0.08)" } : undefined}
          >
            {msg.content}
          </div>
          {msg.files?.length > 0 && (
            <div style={{ marginTop: 4 }}>
              {msg.files.map((f, i) => (
                <div key={i} className={classes.fileAttachment}>
                  <FileText size={14} />
                  <span>{f}</span>
                  <span style={{ fontSize: "0.65rem", opacity: 0.6 }}>(enviaria)</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={classes.container}>
      <div className={classes.chatHeader}>
        <div className={classes.headerLeft}>
          <Bot size={16} />
          {agentName || "Chat de Teste"}
          <span style={{ fontWeight: 400, fontSize: "0.75rem", opacity: 0.7 }}>
            Mensagens não são salvas
          </span>
        </div>
        <Tooltip title="Limpar conversa">
          <IconButton size="small" onClick={handleClear} style={{ color: "#64748b" }}>
            <Trash2 size={16} />
          </IconButton>
        </Tooltip>
      </div>

      <div className={classes.messagesArea}>
        {messages.length === 0 && !loading && (
          <div className={classes.emptyState}>
            <Bot size={48} />
            <div className={classes.emptyText}>
              Envie uma mensagem para testar o agente.
            </div>
          </div>
        )}
        {messages.map(renderMessage)}
        {loading && (
          <div className={classes.typingIndicator}>
            <div className={classes.typingDots}><span /><span /><span /></div>
            Pensando...
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className={classes.inputRow}>
        <TextField
          className={classes.textField}
          variant="outlined"
          placeholder="Digite uma mensagem..."
          multiline
          maxRows={4}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          size="small"
        />

        <IconButton className={classes.sendBtn} onClick={handleSend} disabled={loading || !input.trim()} size="small">
          {loading ? <CircularProgress size={18} style={{ color: "#fff" }} /> : <Send size={18} />}
        </IconButton>
      </div>
    </div>
  );
};

export default TestChatTab;
